// frontend/src/pages/HousingStockModellerPage.tsx

import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useBranding } from "../core/branding/BrandingContext";
import { useHousingState } from "../features/citysense/housing-stock-modeller/hooks/useHousingState";
import { TabBar } from "../features/citysense/housing-stock-modeller/components/TabBar";
import { HousingMap } from "../features/citysense/housing-stock-modeller/components/HousingMap";
import { ScenarioPanel } from "../features/citysense/housing-stock-modeller/components/ScenarioPanel";
import { DeprivationPanel } from "../features/citysense/housing-stock-modeller/components/DeprivationPanel";
import { OptimizerPanel } from "../features/citysense/housing-stock-modeller/components/OptimizerPanel";
import { SummaryTable } from "../features/citysense/housing-stock-modeller/components/SummaryTable";
import { LegendBar } from "../features/citysense/housing-stock-modeller/components/LegendBar";
import { RefreshCw } from "lucide-react";
import { RetrofitReferenceTable } from "../features/citysense/housing-stock-modeller/components/RetrofitReferenceTable";

export const HousingStockModellerPage: React.FC = () => {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const {
    isLoading,
    viewMode,
    setViewMode,
    workingPolygons,
    archetypeColors,
    selectedPolygonId,
    setSelectedPolygonId,
    upgradedByOptimizer,
    summary,
    handleArchetypeChange,
    handleReset,
    handleRunOptimizer,
  } = useHousingState();

  const selectedPolygon =
    workingPolygons.find((p) => p.id === selectedPolygonId) || null;
  const uniqueArchetypes = useMemo(
    () => Array.from(new Set(workingPolygons.map((p) => p.archetype))).sort(),
    [workingPolygons],
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-700 text-lg font-semibold">
            Loading Housing Data...
          </p>
          <p className="text-gray-500 text-sm mt-2">
            Loading Dublin housing stock polygons...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header
        className="flex items-center justify-between px-6 py-3 shadow-md z-10"
        style={{ backgroundColor: branding?.primaryColor || "#0D9488" }}
      >
        <div className="flex items-center space-x-4">
          {branding?.logoUrl && (
            <img
              src={branding.logoUrl}
              alt="Logo"
              className="h-8 object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          )}
          <h1 className="text-lg font-semibold text-white">
            Housing Stock Modeller
          </h1>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg text-sm font-semibold transition-colors"
            title="Reset to original data"
          >
            <RefreshCw className="w-4 h-4" />
            <span className="hidden sm:inline">Reset</span>
          </button>
          <button
            onClick={() => navigate("/dashboard")}
            className="px-4 py-2 bg-white rounded-lg text-sm font-semibold hover:bg-gray-100 transition-colors"
            style={{ color: branding?.primaryColor || "#0D9488" }}
          >
            Back to Dashboard
          </button>
        </div>
      </header>

      {/* Tab Bar */}
      <TabBar activeMode={viewMode} onModeChange={setViewMode} />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Map + Legend (hidden on Reference tab) */}
          {viewMode !== "reference" && (
            <div className="lg:col-span-2 space-y-4">
              <HousingMap
                polygons={workingPolygons}
                archetypeColors={archetypeColors}
                selectedPolygonId={selectedPolygonId}
                upgradedByOptimizer={upgradedByOptimizer}
                viewMode={viewMode}
                onPolygonClick={setSelectedPolygonId}
              />

              <LegendBar
                viewMode={viewMode}
                archetypeColors={archetypeColors}
                showUpgradedLegend={upgradedByOptimizer.size > 0}
              />
            </div>
          )}

          {/* Right Column - Mode-specific Panel */}
          <div className={`space-y-6 ${viewMode === "reference" ? "lg:col-span-3" : ""}`}>
            {viewMode === "scenario" && (
              <>
                <ScenarioPanel
                  selectedPolygon={selectedPolygon}
                  archetypes={uniqueArchetypes}
                  archetypeColors={archetypeColors}
                  onArchetypeChange={handleArchetypeChange}
                />
                <SummaryTable summary={summary} archetypeColors={archetypeColors} />
              </>
            )}

            {viewMode === "deprivation" && (
              <>
                <DeprivationPanel />
                <SummaryTable summary={summary} archetypeColors={archetypeColors} />
              </>
            )}

            {viewMode === "optimizer" && (
              <>
                <OptimizerPanel
                  archetypes={uniqueArchetypes}
                  onRun={handleRunOptimizer}
                />
                <SummaryTable summary={summary} archetypeColors={archetypeColors} />
              </>
            )}

            {viewMode === "reference" && (
              <RetrofitReferenceTable />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};